import React from "react";
import { Icon } from "../Icon";
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../Tooltip";

interface ContextHelpProps {
  ariaLabel: string;
  whatIs: string;
  formula?: string;
  period?: string;
  note?: string;
  noteClass?: string;
  className?: string;
}

const ContextHelpContent = ({
  whatIs,
  formula,
  period,
  note,
  noteClass,
}: ContextHelpProps) => (
  <div className="space-y-2">
    <p className="text-popover-foreground text-[12px] leading-5">{whatIs}</p>
    {formula || period || note ? (
      <div className="border-border space-y-1 border-t pt-2">
        {formula ? (
          <p className="text-muted-foreground text-[11px] leading-4">
            {formula}
          </p>
        ) : null}
        {period ? (
          <p className="text-muted-foreground text-[11px] leading-4">
            {period}
          </p>
        ) : null}
        {note ? (
          <p
            className={`text-[11px] leading-4 ${noteClass || "text-muted-foreground"}`}
          >
            {note}
          </p>
        ) : null}
      </div>
    ) : null}
  </div>
);

type TriggerButtonProps = Pick<ContextHelpProps, "ariaLabel" | "className"> &
  Omit<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    "aria-label" | "className"
  >;

const TriggerButton = React.forwardRef<HTMLButtonElement, TriggerButtonProps>(
  ({ ariaLabel, className = "", type = "button", ...buttonProps }, ref) => (
    <button
      ref={ref}
      type={type}
      aria-label={ariaLabel}
      className={`text-muted-foreground hover:bg-accent hover:text-accent-foreground focus-visible:ring-ring -my-1.5 inline-flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none motion-reduce:transition-none sm:-my-1 sm:h-7 sm:w-7 ${className}`}
      {...buttonProps}
    >
      <Icon name="Info" className="h-4 w-4" />
    </button>
  ),
);
TriggerButton.displayName = "ContextHelpTriggerButton";

const ContextHelp: React.FC<ContextHelpProps> = (props) => {
  if (!props.whatIs) {
    return null;
  }

  return (
    <TooltipProvider delayDuration={0} skipDelayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <TriggerButton
            ariaLabel={props.ariaLabel}
            className={props.className}
          />
        </TooltipTrigger>
        <TooltipContent side="top" align="center" sideOffset={10}>
          <ContextHelpContent {...props} />
          <TooltipArrow className="fill-popover" />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export { ContextHelp };
